'use client';

import React, { useEffect, useState, useRef } from 'react';
import { LEVEL_CONFIGS } from './PhaserGame';

interface LevelCompleteScreenProps {
  completedLevel: number;
  score: number;
  onNextLevel: () => void;
  onReplay: () => void;
}

// Rank thresholds
function getRank(score: number): { label: string; color: string; glow: string; emoji: string; description: string } {
  if (score >= 50000) return { label: 'S', color: '#ffd60a', glow: 'rgba(255,214,10,0.7)', emoji: '👑', description: 'LEGENDARY' };
  if (score >= 30000) return { label: 'A', color: '#00e5ff', glow: 'rgba(0,229,255,0.6)', emoji: '⚡', description: 'ELITE' };
  if (score >= 15000) return { label: 'B', color: '#a78bfa', glow: 'rgba(167,139,250,0.6)', emoji: '🔥', description: 'SKILLED' };
  if (score >= 7000)  return { label: 'C', color: '#34d399', glow: 'rgba(52,211,153,0.5)', emoji: '🛹', description: 'SOLID' };
  return                     { label: 'D', color: '#94a3b8', glow: 'rgba(148,163,184,0.4)', emoji: '🤙', description: 'KEEP RIDING' };
}

// Confetti particle
interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  shape: 'rect' | 'circle' | 'star';
  opacity: number;
}

const CONFETTI_COLORS = ['#ffd60a', '#00e5ff', '#ff6b6b', '#a78bfa', '#34d399', '#fb923c', '#f472b6'];

export default function LevelCompleteScreen({ completedLevel, score, onNextLevel, onReplay }: LevelCompleteScreenProps) {
  const isLastLevel = completedLevel >= 4;
  const nextCfg = !isLastLevel ? LEVEL_CONFIGS[completedLevel] : null;
  const rank = getRank(score);

  const [visible, setVisible] = useState(false);
  const [scoreDisplay, setScoreDisplay] = useState(0);
  const [rankVisible, setRankVisible] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const animFrameRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const lastTimeRef = useRef<number>(0);

  // Spawn confetti particles
  useEffect(() => {
    const newParticles: Particle[] = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -10 - Math.random() * 20,
      vx: (Math.random() - 0.5) * 1.2,
      vy: 1.5 + Math.random() * 2.5,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      shape: (['rect', 'circle', 'star'] as const)[Math.floor(Math.random() * 3)],
      opacity: 1,
    }));
    particlesRef.current = newParticles;
    setParticles([...newParticles]);

    const animate = (time: number) => {
      const dt = Math.min((time - lastTimeRef.current) / 16, 3);
      lastTimeRef.current = time;
      particlesRef.current = particlesRef.current.map(p => ({
        ...p,
        x: p.x + p.vx * dt,
        y: p.y + p.vy * dt,
        rotation: p.rotation + p.rotationSpeed * dt,
        vy: p.vy + 0.04 * dt,
        opacity: p.y > 110 ? Math.max(0, p.opacity - 0.05 * dt) : p.opacity,
      })).filter(p => p.opacity > 0);
      setParticles([...particlesRef.current]);
      if (particlesRef.current.length > 0) {
        animFrameRef.current = requestAnimationFrame(animate);
      }
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, []);

  // Staggered entrance
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 80);
    // Animate score count-up
    const t2 = setTimeout(() => {
      const duration = 1200;
      const start = Date.now();
      const tick = () => {
        const elapsed = Date.now() - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setScoreDisplay(Math.floor(eased * score));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, 400);
    const t3 = setTimeout(() => setRankVisible(true), 700);
    const t4 = setTimeout(() => setButtonsVisible(true), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [score]);

  return (
    <div
      className="absolute inset-0 z-[70] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(10px)' }}
    >
      {/* Confetti layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.shape === 'rect' ? p.size * 0.5 : p.size,
              background: p.shape === 'star' ? 'transparent' : p.color,
              borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'rect' ? 2 : 0,
              transform: `rotate(${p.rotation}deg)`,
              opacity: p.opacity,
              fontSize: p.shape === 'star' ? p.size + 4 : undefined,
              color: p.color,
              lineHeight: 1,
            }}
          >
            {p.shape === 'star' ? '★' : null}
          </div>
        ))}
      </div>

      {/* Ambient glow behind card */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${rank.glow} 0%, transparent 70%)`,
          opacity: 0.35,
          transform: 'translate(-50%, -50%)',
          left: '50%',
          top: '50%',
        }}
      />

      {/* Main card */}
      <div
        style={{
          background: 'linear-gradient(160deg, rgba(14,14,20,0.99) 0%, rgba(22,16,36,0.99) 100%)',
          border: `2px solid ${rank.color}55`,
          borderRadius: 28,
          padding: '32px 36px 28px',
          maxWidth: 400,
          width: '92%',
          textAlign: 'center',
          boxShadow: `0 0 80px ${rank.glow}33, 0 24px 60px rgba(0,0,0,0.7)`,
          position: 'relative',
          zIndex: 1,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.88) translateY(24px)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.35s ease',
        }}
      >
        {/* Top label */}
        <div
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 900,
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginBottom: 14,
          }}
        >
          {isLastLevel ? '🎊 ALL LEVELS CLEARED' : `LEVEL ${completedLevel} COMPLETE`}
        </div>

        {/* Level name */}
        <div
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 800,
            fontSize: '1.25rem',
            color: '#fff',
            marginBottom: 20,
            letterSpacing: '0.04em',
          }}
        >
          {LEVEL_CONFIGS[completedLevel - 1]?.name ?? ''}
        </div>

        {/* Rank badge */}
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 22,
            transform: rankVisible ? 'scale(1) rotate(0deg)' : 'scale(0.4) rotate(-20deg)',
            opacity: rankVisible ? 1 : 0,
            transition: 'transform 0.5s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease',
          }}
        >
          <div
            style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 35%, ${rank.color}33 0%, rgba(0,0,0,0.6) 100%)`,
              border: `3px solid ${rank.color}`,
              boxShadow: `0 0 30px ${rank.glow}, inset 0 0 20px ${rank.color}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 8,
              position: 'relative',
            }}
          >
            {/* Rank letter */}
            <span
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontWeight: 900,
                fontSize: '2.8rem',
                color: rank.color,
                lineHeight: 1,
                textShadow: `0 0 20px ${rank.glow}`,
              }}
            >
              {rank.label}
            </span>
            {/* Emoji badge corner */}
            <span
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                fontSize: 22,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
              }}
            >
              {rank.emoji}
            </span>
          </div>
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 800,
              fontSize: '0.7rem',
              color: rank.color,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {rank.description}
          </span>
        </div>

        {/* Score display */}
        <div
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 14,
            padding: '14px 20px',
            marginBottom: 20,
          }}
        >
          <div
            style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              marginBottom: 6,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            Final Score
          </div>
          <div
            style={{
              color: rank.color,
              fontWeight: 900,
              fontSize: '2.4rem',
              fontFamily: 'DM Sans, sans-serif',
              lineHeight: 1,
              textShadow: `0 0 24px ${rank.glow}`,
              letterSpacing: '-0.02em',
            }}
          >
            {scoreDisplay.toLocaleString()}
          </div>
        </div>

        {/* Level progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 24 }}>
          {[1, 2, 3, 4].map(lvl => (
            <div
              key={lvl}
              style={{
                width: lvl === completedLevel ? 28 : 10,
                height: 10,
                borderRadius: 5,
                background: lvl < completedLevel
                  ? '#00e5ff'
                  : lvl === completedLevel
                  ? rank.color
                  : 'rgba(255,255,255,0.12)',
                boxShadow: lvl === completedLevel ? `0 0 10px ${rank.glow}` : 'none',
                transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div
          style={{
            transform: buttonsVisible ? 'translateY(0)' : 'translateY(16px)',
            opacity: buttonsVisible ? 1 : 0,
            transition: 'transform 0.4s ease, opacity 0.4s ease',
          }}
        >
          {isLastLevel ? (
            <>
              <div
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 13,
                  marginBottom: 16,
                  lineHeight: 1.6,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                You&apos;ve conquered all levels! 🎊<br />
                <span style={{ color: rank.color, fontWeight: 700 }}>Unlock more for just $1.</span>
              </div>
              <button
                onClick={onNextLevel}
                style={{
                  width: '100%',
                  background: `linear-gradient(135deg, ${rank.color} 0%, #ff9f43 100%)`,
                  border: 'none',
                  borderRadius: 14,
                  padding: '15px 0',
                  color: '#0d0d0f',
                  fontWeight: 900,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: `0 4px 20px ${rank.glow}55`,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              >
                🔓 Unlock More — $1
              </button>
            </>
          ) : (
            <>
              {nextCfg && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    background: 'rgba(0,229,255,0.06)',
                    border: '1px solid rgba(0,229,255,0.18)',
                    borderRadius: 10,
                    padding: '8px 14px',
                    marginBottom: 14,
                  }}
                >
                  <span style={{ fontSize: 14 }}>🔓</span>
                  <span
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    Next:{' '}
                    <span style={{ color: '#00e5ff', fontWeight: 700 }}>
                      Level {completedLevel + 1} — {nextCfg.name}
                    </span>
                  </span>
                </div>
              )}
              <button
                onClick={onNextLevel}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #00e5ff 0%, #0077ff 100%)',
                  border: 'none',
                  borderRadius: 14,
                  padding: '15px 0',
                  color: '#0d0d0f',
                  fontWeight: 900,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 20px rgba(0,229,255,0.35)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.03)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              >
                Next Level →
              </button>
            </>
          )}

          <button
            onClick={onReplay}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 14,
              padding: '13px 0',
              color: 'rgba(255,255,255,0.65)',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              letterSpacing: '0.04em',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)';
              (e.currentTarget as HTMLButtonElement).style.color = '#fff';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
              (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.65)';
            }}
          >
            🔄 Retry Level {completedLevel}
          </button>
        </div>
      </div>
    </div>
  );
}
